import Head from 'next/head';
import { useRouter } from 'next/router';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import getConfig from 'next/config';

import { Text, Link } from '@terminal/design-system';
import Layout from '@components/Layout';
import metaRobots from '@helpers/metaRobots';
import sharecardImage from '@public/images/sharecard.jpg';
import sitePath from '@helpers/sitePath';
import { SolutionsForLayout } from '@components/SolutionsForLayout';

const { publicRuntimeConfig } = getConfig();
const { talentHubOnboardingUrl } = publicRuntimeConfig;

const hero = {
  title: {
    main: 'The all-in-one developer hiring solution for',
    highlight: 'founders',
  },
  text: 'Simple, stress-free developer hiring to supercharge your growth. Hire elite talent faster at lower costs from our pool of 100K+ global software engineers.',
  image: {
    sourceUrl: '/images/solutions-for/founders.png',
    altText: 'Man smiling with a plant and dashboard behind him',
    size: {
      width: 530,
      height: 468,
    },
  },
  button: {
    url: talentHubOnboardingUrl,
    text: 'Browse Talent',
    width: '15rem',
  },
};

const offerings = {
  image: {
    src: '/images/build-your-team/hire.svg',
    alt: 'Woman in front of a computer',
  },
  content: [
    {
      title: "You need elite developers, we've got them",
      description:
        'We have a deep pool of 100K+ elite software developers, rigorously sourced and vetted. We specialize in finding great talent for startups.',
    },
    {
      title: 'Hire talent 35% faster at 40% lower costs',
      description:
        'Our always-on recruiting pipeline, smart talent platform, and global talent ensures you save time and money over traditional U.S. recruiting.',
    },
    {
      title: 'Flexible hiring with full-time or contract roles',
      formattedText: (
        <Text variant="body" mb={9}>
          Terminal is the only talent platform solution that offers both{' '}
          <Link display="unset" textDecor="underline" color="text.link" href="/hiring-plans">
            full-time and contract global developers
          </Link>
          . We also offer contract-to-hire options.
        </Text>
      ),
      description: '',
    },
    {
      title: 'Our full-stack solution both hires & employs',
      description:
        'Sourcing, recruiting, and global employment can be intense.  We take care of everything so you can focus your team on building and growth.',
    },
  ],
  button: {
    url: talentHubOnboardingUrl,
    text: 'Hire Software Developers',
  },
};

const footer = {
  longText: false,
  text: 'Find the scale you need. Build your team today.',
  image: '/images/home/businesses-footer.jpg',
};

const meta = {
  title: 'Solutions for Founders to hire software engineers remotely - Terminal.io',
  description:
    "Startup founders need simple and cost-effective solutions to hire software engineers. Terminal's all-in-one solution handles global hiring and employment.",
};

export async function getServerSideProps({ locale }) {
  return {
    props: {
      ...(await serverSideTranslations(locale)),
    },
  };
}

export default function ForFounders() {
  const router = useRouter();

  return (
    <>
      <Head>
        <link rel="canonical" href={sitePath(router.asPath)} />
        <title>{meta.title}</title>
        <meta property="og:title" content={meta.title} />
        <meta name="description" content={meta.description} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={sitePath(router.asPath)} />
        <meta property="og:image" content={sharecardImage.src} />
        <meta name="robots" content={metaRobots({ translated: false, locale: router.locale })} />
      </Head>
      <Layout type="businesses" footer={footer} withCTA>
        <SolutionsForLayout hero={hero} offerings={offerings} />
      </Layout>
    </>
  );
}
